import * as React from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { Plus, Pencil, Trash2, Music } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { ConfirmDialog } from '@/components/data/ConfirmDialog';
import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { CollectionForm } from '@/features/stories/CollectionForm';
import { AddItemForm as AddStoryForm } from '@/features/stories/AddItemForm';
import { NarrationForm } from '@/features/stories/NarrationForm';
import { useDeleteStoryCollection, useStoryCollections } from '@/queries/stories';
import { useApiError } from '@/lib/useApiError';
import {
  storyCollectionStateSchema,
  type StoryCollection,
  type StoryCollectionState,
} from '@/schemas/admin/stories';

const searchSchema = z.object({
  state: storyCollectionStateSchema.optional(),
});

export const Route = createFileRoute('/_authed/curation/stories')({
  validateSearch: searchSchema,
  component: StoriesScreen,
});

function StoriesScreen() {
  const { t } = useTranslation('stories');
  const { t: tc } = useTranslation('common');
  const search = Route.useSearch();
  const navigate = useNavigate();
  const collections = useStoryCollections();
  const resolveError = useApiError();
  const deleteCollection = useDeleteStoryCollection();

  const [creating, setCreating] = React.useState(false);
  const [editing, setEditing] = React.useState<StoryCollection | null>(null);
  const [confirmDelete, setConfirmDelete] = React.useState<StoryCollection | null>(null);
  const [addingItemFor, setAddingItemFor] = React.useState<StoryCollection | null>(null);
  const [narrationFor, setNarrationFor] = React.useState<{ collection: StoryCollection; storyId: string } | null>(null);

  const filtered = React.useMemo(() => {
    if (!collections.data) return [];
    if (!search.state) return collections.data;
    return collections.data.filter((c) => c.state === search.state);
  }, [collections.data, search.state]);

  function setStateFilter(next: StoryCollectionState | undefined) {
    void navigate({
      to: '/curation/stories',
      search: (prev) => ({ ...prev, state: next }),
      replace: true,
    });
  }

  async function handleDelete() {
    if (!confirmDelete) return;
    try {
      await deleteCollection.mutateAsync(confirmDelete.id);
      toast.success(t('delete.done'));
      setConfirmDelete(null);
    } catch (err) {
      const resolved = resolveError(err);
      toast.error(resolved.message, resolved.recovery ? { description: resolved.recovery } : undefined);
    }
  }

  const stateOptions: { value: StoryCollectionState | undefined; key: string }[] = [
    { value: undefined, key: 'all' },
    { value: 'draft', key: 'draft' },
    { value: 'published', key: 'published' },
    { value: 'archived', key: 'archived' },
  ];

  const columns: readonly DataColumn<StoryCollection>[] = [
    {
      id: 'slug',
      header: t('form.slug'),
      cell: (r) => (
        <span dir="ltr" className="font-mono text-xs">
          {r.slug}
        </span>
      ),
      sortBy: (r) => r.slug,
    },
    {
      id: 'title',
      header: t('form.title'),
      cell: (r) => <span>{r.title_en ?? '—'}</span>,
    },
    {
      id: 'state',
      header: t('form.state'),
      cell: (r) => (
        <Badge tone={r.state === 'published' ? 'success' : r.state === 'archived' ? 'neutral' : 'info'}>
          {t(`states.${r.state}`)}
        </Badge>
      ),
      sortBy: (r) => r.state,
    },
    {
      id: 'actions',
      header: <span className="sr-only">{tc('table.actions', { defaultValue: 'Actions' })}</span>,
      align: 'end',
      cell: (r) => (
        <div className="flex justify-end gap-1">
          <Button size="sm" variant="ghost" onClick={() => setAddingItemFor(r)}>
            <Plus className="size-4" aria-hidden />
            <span className="sr-only md:not-sr-only md:text-xs">{t('items.addStory')}</span>
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(r)}>
            <Pencil className="size-4" aria-hidden />
            <span className="sr-only md:not-sr-only md:text-xs">{t('editCollection')}</span>
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(r)}>
            <Trash2 className="size-4" aria-hidden />
            <span className="sr-only">{t('delete.confirmTitle')}</span>
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
          <p className="max-w-2xl text-sm text-[color:var(--color-fg-muted)]">{t('subtitle')}</p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="size-4" aria-hidden />
          {t('newCollection')}
        </Button>
      </header>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('filterState')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div role="tablist" className="flex flex-wrap gap-2">
            {stateOptions.map((opt) => {
              const selected = (search.state ?? undefined) === opt.value;
              return (
                <button
                  key={opt.key}
                  role="tab"
                  type="button"
                  aria-selected={selected}
                  onClick={() => setStateFilter(opt.value)}
                  className={`rounded-[var(--radius-pill)] px-3 py-1 text-xs font-semibold ${
                    selected
                      ? 'bg-[color:var(--color-accent)] text-[color:var(--color-accent-fg)]'
                      : 'bg-[color:var(--color-surface-2)] text-[color:var(--color-fg-muted)]'
                  }`}
                >
                  {t(`states.${opt.key}`)}
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <DataTable<StoryCollection>
        data={filtered}
        columns={columns}
        rowKey={(r) => r.id}
        loading={collections.isLoading}
        error={collections.error ? resolveError(collections.error) : null}
        empty={{ title: t('empty'), body: t('emptyBody') }}
        initialSort={{ id: 'slug', direction: 'asc' }}
      />

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('newCollection')}</DialogTitle>
            <DialogDescription>
              {t('newCollectionBody', {
                defaultValue:
                  'Create a slug-keyed story collection. Slug is immutable once saved.',
              })}
            </DialogDescription>
          </DialogHeader>
          <CollectionForm onSaved={() => setCreating(false)} onCancel={() => setCreating(false)} />
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editing)} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('editCollection')}</DialogTitle>
            <DialogDescription>
              {t('editCollectionBody', {
                defaultValue:
                  'Update titles, state, and metadata. Slug stays locked to keep tagged stories linked.',
              })}
            </DialogDescription>
          </DialogHeader>
          {editing ? (
            <CollectionForm
              collection={editing}
              onSaved={() => setEditing(null)}
              onCancel={() => setEditing(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(addingItemFor)} onOpenChange={(o) => !o && setAddingItemFor(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('items.addTitle')}</DialogTitle>
            <DialogDescription>{t('items.addBody')}</DialogDescription>
          </DialogHeader>
          {addingItemFor ? (
            <AddStoryForm
              collectionId={addingItemFor.id}
              onSaved={() => setAddingItemFor(null)}
              onCancel={() => setAddingItemFor(null)}
            />
          ) : null}
          {addingItemFor ? (
            <div className="mt-4 border-t border-[color:var(--color-border)] pt-3">
              <NarrationLauncher
                onLaunch={(storyId) =>
                  setNarrationFor({ collection: addingItemFor, storyId })
                }
              />
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(narrationFor)} onOpenChange={(o) => !o && setNarrationFor(null)}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{t('narration.title')}</DialogTitle>
            <DialogDescription>
              {t('narration.body', {
                defaultValue:
                  'Attach or update narration audio metadata for the selected story.',
              })}
            </DialogDescription>
          </DialogHeader>
          {narrationFor ? (
            <NarrationForm
              storyId={narrationFor.storyId}
              onSaved={() => setNarrationFor(null)}
              onCancel={() => setNarrationFor(null)}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirmDelete)}
        onOpenChange={(o) => !o && setConfirmDelete(null)}
        title={t('delete.confirmTitle')}
        description={t('delete.confirmBody')}
        destructive
        loading={deleteCollection.isPending}
        onConfirm={handleDelete}
      />
    </div>
  );
}

function NarrationLauncher({ onLaunch }: { onLaunch: (storyId: string) => void }) {
  const { t } = useTranslation('stories');
  const [id, setId] = React.useState('');
  return (
    <div className="space-y-2">
      <label className="text-xs font-medium uppercase tracking-wide text-[color:var(--color-fg-muted)]">
        {t('narration.open')}
      </label>
      <div className="flex gap-2">
        <input
          dir="ltr"
          value={id}
          onChange={(e) => setId(e.target.value)}
          placeholder="story UUID"
          className="block h-10 flex-1 rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 font-mono text-xs"
        />
        <Button
          type="button"
          variant="secondary"
          disabled={!/^[0-9a-f-]{36}$/i.test(id)}
          onClick={() => onLaunch(id)}
        >
          <Music className="size-4" aria-hidden />
          {t('narration.title')}
        </Button>
      </div>
    </div>
  );
}

